import { cloneDeep, get, set } from "lodash";
import { useMemo, useState } from "react";

import type { ClientWithConfig, ClientConfig } from "@platform/dmv-api";

/**
 * Formats a config value for display in the before/after diff.
 * Primitives are JSON-stringified inline; objects are pretty-printed.
 */
const formatValue = (v: unknown): string => {
  if (v === undefined) return "undefined";
  if (v === null) return "null";
  if (typeof v !== "object") return JSON.stringify(v);
  return JSON.stringify(v, null, 2);
};

type ClientDiffProps = {
  client: ClientWithConfig;
  selectedPath: string;
  currentValue: unknown;
  newValue: unknown;
};


export type PropagationModalProps = {
  selectedClients: ClientWithConfig[];
  selectedPath: string;
  modelValue: unknown;
  onConfirm: (_patches: Partial<ClientWithConfig>[]) => void;
  onClose: () => void;
  isSubmitting: boolean;
};

/**
 * Renders a before/after diff for a single destination client,
 * showing only the config path that will change.
 */
const ClientDiff = ({
  client,
  selectedPath,
  currentValue,
  newValue,
}: ClientDiffProps) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border border-gray-200 rounded-md overflow-hidden">
      <button
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 text-left"
      >
        <div className="flex items-center gap-x-2 min-w-0">
          <span className="text-sm font-medium text-gray-800 truncate">
            {client.displayName}
          </span>
          <span className="text-xs text-gray-400 font-mono truncate">
            {client.id}
          </span>
        </div>
        <span className="text-xs text-gray-400 ml-2 shrink-0">
          {expanded ? "▾" : "▸"}
        </span>
      </button>

      {expanded && (
        <div className="px-3 py-2 text-xs font-mono space-y-2">
          <div className="text-gray-500 mb-1">
            Path: <span className="text-black font-medium">{selectedPath}</span>
          </div>
          <div className="grid grid-cols-2 gap-x-3">
            <div>
              <div className="text-red-600 font-semibold mb-1">Before</div>
              <pre className="bg-red-50 text-red-700 rounded px-2 py-1.5 overflow-x-auto whitespace-pre-wrap break-all">
                {formatValue(currentValue)}
              </pre>
            </div>
            <div>
              <div className="text-green-700 font-semibold mb-1">After</div>
              <pre className="bg-green-50 text-green-700 rounded px-2 py-1.5 overflow-x-auto whitespace-pre-wrap break-all">
                {formatValue(newValue)}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Deep-merges a new value at a dot-notation path into an existing config object.
 * Arrays from the source replace rather than merge (consistent with DMV server behaviour).
 */
const buildMergedConfig = (
  existingConfig: ClientConfig | undefined,
  path: string,
  newValue: unknown
): Record<string, unknown> => {
  const draft = cloneDeep(existingConfig ?? {}) as Record<string, unknown>;
  set(draft, path, cloneDeep(newValue));
  return draft;
};


/**
 * Propagation confirmation modal.
 *
 * Shows a list of destination clients that will be updated, each with a
 * before/after diff for the selected config path. User must click a clearly
 * labeled button to confirm the batch update.
 */
const PropagationModal = ({
  selectedClients,
  selectedPath,
  modelValue,
  onConfirm,
  onClose,
  isSubmitting,
}: PropagationModalProps) => {
  // Memoized: buildMergedConfig calls cloneDeep per client, so avoid
  // recomputing on every parent re-render while the modal is open.
  const patches = useMemo(
    () =>
      selectedClients.map((client) => ({
        client,
        currentValue: get(client.config, selectedPath),
        mergedConfig: buildMergedConfig(
          client.config as ClientConfig,
          selectedPath,
          modelValue
        ),
      })),
    [selectedClients, selectedPath, modelValue]
  );

  const handleConfirm = () => {
    const payload: Partial<ClientWithConfig>[] = patches.map(
      ({ client, mergedConfig }) => ({
        id: client.id,
        config: mergedConfig as ClientConfig,
      })
    );
    onConfirm(payload);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 flex flex-col max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-semibold">Confirm propagation</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Propagating{" "}
              <span className="font-mono text-black">{selectedPath}</span> to{" "}
              {selectedClients.length} client
              {selectedClients.length !== 1 ? "s" : ""}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          >
            ✕
          </button>
        </div>

        {/* Model value */}
        <div className="px-6 py-3 bg-blue-50 border-b border-blue-100">
          <p className="text-xs text-blue-700 font-medium mb-1">
            New value (from model)
          </p>
          <pre className="text-xs font-mono text-blue-800 whitespace-pre-wrap break-all">
            {formatValue(modelValue)}
          </pre>
        </div>

        {/* Client diffs — scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2">
          <p className="text-xs text-gray-500 mb-2">
            Click a client row to expand the before/after diff.
          </p>
          {patches.map(({ client, currentValue }) => (
            <ClientDiff
              key={client.id}
              client={client}
              selectedPath={selectedPath}
              currentValue={currentValue}
              newValue={modelValue}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-x-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium bg-black text-white rounded-md hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-x-2"
          >
            {isSubmitting ? (
              <>
                <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Updating…
              </>
            ) : (
              `Propagate to ${selectedClients.length} client${
                selectedClients.length !== 1 ? "s" : ""
              }`
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PropagationModal;
